import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import Portal from '../../shared/Portal';
import {
  getInvoices, createInvoice, updateInvoice, deleteInvoice, sendInvoice,
  getBills, createBill, updateBill, deleteBill,
  getExpenses, createExpense, updateExpense, deleteExpense,
  getJournal, createJournalEntry, getLedgerBalance, getLedgerPL,
  getAccountingConnections, saveAccountingConnection, syncAccountingConnection, deleteAccountingConnection,
  getCustomers,
} from './businessApi';

// ── Palette ───────────────────────────────────────────────────────────────────
const F = {
  bg: '#14141a', panel: '#1e1e28',
  border: 'rgba(255,255,255,0.1)', input: '#12121a',
  inputBorder: 'rgba(255,255,255,0.15)',
};

const inp = (x = {}) => ({
  background: F.input, border: `1px solid ${F.inputBorder}`,
  borderRadius: 10, padding: '10px 14px', color: C.white,
  fontSize: 13, width: '100%', boxSizing: 'border-box',
  outline: 'none', fontFamily: 'inherit', ...x,
});
const panel = (x = {}) => ({
  background: F.panel, border: `1px solid ${F.border}`,
  borderRadius: 14, padding: 16, marginBottom: 12, ...x,
});
const label = { fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 6, display: 'block' };
const row   = { display: 'flex', gap: 10, marginBottom: 12 };

const SUBTABS = [
  { id: 'invoices',    nl: 'Facturen',    en: 'Invoices' },
  { id: 'bills',       nl: 'Rekeningen',  en: 'Bills' },
  { id: 'expenses',    nl: 'Kosten',      en: 'Expenses' },
  { id: 'ledger',      nl: 'Grootboek',   en: 'Ledger' },
  { id: 'connections', nl: 'Koppelingen', en: 'Connections' },
];

const EXPENSE_CATS = ['office','travel','software','marketing','utilities','payroll','rent','other'];
const BILL_CATS    = ['supplier','utilities','rent','insurance','service','other'];
const CURRENCIES   = ['EUR','USD','GBP'];

const ACCOUNTING_PROVIDERS = [
  { id: 'moneybird', name: 'Moneybird',  icon: '🐦', color: '#1DB8C2', fields: [{ key: 'api_token', label: 'API Token', type: 'password' }, { key: 'administration_id', label: 'Administration ID' }] },
  { id: 'snelstart', name: 'SnelStart',  icon: '⚡', color: '#FF6B35', fields: [{ key: 'api_key', label: 'API Key', type: 'password' }, { key: 'client_id', label: 'Client ID' }] },
  { id: 'quickbooks',name: 'QuickBooks', icon: '📊', color: '#2CA01C', fields: [{ key: 'access_token', label: 'Access Token', type: 'password' }, { key: 'realm_id', label: 'Realm / Company ID' }] },
  { id: 'exact',     name: 'Exact Online',icon: '🔵', color: '#004C97', fields: [{ key: 'access_token', label: 'Access Token', type: 'password' }, { key: 'division', label: 'Division Number' }] },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtEuro(n) { return `€ ${parseFloat(n || 0).toFixed(2)}`; }
function today()    { return new Date().toISOString().slice(0, 10); }
function nextInvNum(invoices) {
  const nums = invoices.map(i => parseInt((i.invoice_number || '').replace(/\D/g, '') || 0));
  return `INV-${String((Math.max(0, ...nums) + 1)).padStart(4, '0')}`;
}

function calcTotals(items) {
  return items.reduce((acc, it) => {
    const qty   = parseFloat(it.qty || it.quantity || 1);
    const price = parseFloat(it.unit_price || it.price || 0);
    const vat   = parseFloat(it.vat_pct || it.vat || 0);
    const sub   = qty * price;
    const vatAmt= sub * vat / 100;
    return { subtotal: acc.subtotal + sub, vat: acc.vat + vatAmt, total: acc.total + sub + vatAmt };
  }, { subtotal: 0, vat: 0, total: 0 });
}

// ── Sheet (Portal overlay) ────────────────────────────────────────────────────
function Sheet({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <Portal>
      <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.65)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
        onClick={e => e.target === e.currentTarget && onClose()}>
        <style>{`@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}`}</style>
        <div style={{ background: F.bg, borderTop: `1.5px solid ${F.border}`, borderRadius: '20px 20px 0 0', maxHeight: '92vh', display: 'flex', flexDirection: 'column', animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1) both', boxShadow: '0 -8px 40px rgba(0,0,0,0.5)' }}>
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 10px' }}>
            <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{title}</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.silver, fontSize: 22, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px env(safe-area-inset-bottom,24px)', paddingBottom: 36 }}>{children}</div>
        </div>
      </div>
    </Portal>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  draft: '#667799', sent: '#5B9CF6', paid: '#4AC87A', overdue: '#C05050',
  cancelled: '#555', unpaid: '#C8A84B', disputed: '#C05050',
};
function StatusBadge({ status }) {
  const col = STATUS_COLORS[status] || '#888';
  return <span style={{ background: `${col}20`, color: col, border: `1px solid ${col}40`, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{status}</span>;
}

// ── Print Invoice ─────────────────────────────────────────────────────────────
function printInvoice(inv, business) {
  const items = Array.isArray(inv.items) ? inv.items : (typeof inv.items === 'string' ? JSON.parse(inv.items || '[]') : []);
  const totals = calcTotals(items);
  const design = inv.design || {};
  const color  = design.primary_color || '#5B9CF6';

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${inv.invoice_number}</title>
  <style>
    body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#111;background:#fff}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:32px}
    .logo{width:80px;height:80px;object-fit:contain}
    .biz-name{font-size:22px;font-weight:800;color:${color}}
    .inv-meta{text-align:right;font-size:12px;color:#555}
    .inv-num{font-size:18px;font-weight:700;color:${color};margin-bottom:4px}
    .parties{display:flex;gap:40px;margin-bottom:28px}
    .party h4{font-size:11px;letter-spacing:2px;color:#999;margin:0 0 6px}
    .party p{font-size:13px;margin:2px 0;color:#222}
    table{width:100%;border-collapse:collapse;margin-bottom:20px}
    th{background:${color};color:#fff;padding:10px 12px;text-align:left;font-size:12px}
    td{padding:9px 12px;border-bottom:1px solid #eee;font-size:13px}
    .totals{text-align:right;margin-bottom:32px}
    .totals div{margin:4px 0;font-size:13px;color:#555}
    .totals .grand{font-size:18px;font-weight:800;color:${color}}
    .footer{border-top:1px solid #eee;padding-top:16px;font-size:11px;color:#999;text-align:center}
    @media print{body{padding:20px}}
  </style></head><body>
  <div class="header">
    <div>${design.logo_url ? `<img class="logo" src="${design.logo_url}">` : ''}
      <div class="biz-name">${business?.name || ''}</div>
      <div style="font-size:12px;color:#666;margin-top:4px">${business?.address || ''}</div>
    </div>
    <div class="inv-meta">
      <div class="inv-num">${inv.invoice_number}</div>
      <div>Datum: ${inv.issue_date}</div>
      ${inv.due_date ? `<div>Vervaldatum: ${inv.due_date}</div>` : ''}
      <div style="margin-top:6px"><span style="background:${STATUS_COLORS[inv.status]||'#888'};color:#fff;padding:3px 10px;border-radius:6px;font-size:11px;font-weight:700">${inv.status}</span></div>
    </div>
  </div>
  <div class="parties">
    <div class="party"><h4>VAN</h4><p style="font-weight:700">${business?.name || ''}</p></div>
    <div class="party"><h4>AAN</h4><p style="font-weight:700">${inv.customer_name}</p>${inv.customer_email ? `<p>${inv.customer_email}</p>` : ''}${inv.customer_address ? `<p>${inv.customer_address}</p>` : ''}</div>
  </div>
  <table><tr><th>Omschrijving</th><th>Aantal</th><th>Prijs</th><th>BTW</th><th>Totaal</th></tr>
  ${items.map(it => {
    const qty = parseFloat(it.qty||it.quantity||1), price = parseFloat(it.unit_price||it.price||0);
    const vat = parseFloat(it.vat_pct||it.vat||0), line = qty*price;
    return `<tr><td>${it.description||it.name||''}</td><td>${qty}</td><td>${fmtEuro(price)}</td><td>${vat}%</td><td>${fmtEuro(line*(1+vat/100))}</td></tr>`;
  }).join('')}</table>
  <div class="totals">
    <div>Subtotaal: ${fmtEuro(totals.subtotal)}</div>
    <div>BTW: ${fmtEuro(totals.vat)}</div>
    <div class="grand">Totaal: ${fmtEuro(totals.total)}</div>
  </div>
  ${inv.notes ? `<div style="background:#f7f7f7;padding:14px;border-radius:8px;font-size:12px;color:#555;margin-bottom:20px"><b>Notities:</b><br>${inv.notes}</div>` : ''}
  <div class="footer">Gegenereerd door SenseLifting · ${new Date().toLocaleDateString('nl-NL')}</div>
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  setTimeout(() => { w.print(); }, 400);
}

// ═══════════════════════════════════════════════════════════════════════════════
// INVOICES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_INV = {
  invoice_number: '', customer_name: '', customer_email: '', customer_address: '',
  issue_date: today(), due_date: '', status: 'draft', notes: '',
  items: [{ description: '', qty: 1, unit_price: '', vat_pct: 21 }],
  design: { primary_color: '#5B9CF6', logo_url: '', layout: 'classic' },
};

function InvoicesTab({ lang, businessId, business }) {
  const nl = lang === 'nl';
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sheet, setSheet]       = useState(null); // null | { mode:'new'|'edit', inv }
  const [saving, setSaving]     = useState(false);
  const [sending, setSending]   = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    const [inv, custs] = await Promise.all([
      getInvoices(businessId),
      getCustomers(businessId),
    ]);
    setInvoices(Array.isArray(inv) ? inv : []);
    setCustomers(Array.isArray(custs) ? custs : []);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setSheet({ mode: 'new', inv: { ...EMPTY_INV, invoice_number: nextInvNum(invoices) } });
  const openEdit = (inv) => {
    const parsed = { ...inv, items: typeof inv.items === 'string' ? JSON.parse(inv.items || '[]') : (inv.items || []), design: typeof inv.design === 'string' ? JSON.parse(inv.design || '{}') : (inv.design || EMPTY_INV.design) };
    setSheet({ mode: 'edit', inv: parsed });
  };

  const set = (k, v) => setSheet(s => ({ ...s, inv: { ...s.inv, [k]: v } }));
  const setItem = (i, k, v) => setSheet(s => {
    const items = [...s.inv.items]; items[i] = { ...items[i], [k]: v };
    return { ...s, inv: { ...s.inv, items } };
  });
  const addItem = () => setSheet(s => ({ ...s, inv: { ...s.inv, items: [...s.inv.items, { description: '', qty: 1, unit_price: '', vat_pct: 21 }] } }));
  const removeItem = (i) => setSheet(s => ({ ...s, inv: { ...s.inv, items: s.inv.items.filter((_, j) => j !== i) } }));

  const handleSave = async () => {
    if (!sheet) return;
    setSaving(true);
    try {
      if (sheet.mode === 'new') await createInvoice(businessId, sheet.inv);
      else await updateInvoice(businessId, sheet.inv.id, sheet.inv);
      setSheet(null);
      await load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(nl ? 'Factuur verwijderen?' : 'Delete invoice?')) return;
    await deleteInvoice(businessId, id);
    await load();
  };

  const handleSend = async () => {
    if (!sheet) return;
    setSending(true);
    try {
      await sendInvoice(businessId, sheet.inv.id, { email: sheet.inv.customer_email });
      alert(nl ? 'Factuur verstuurd!' : 'Invoice sent!');
    } finally { setSending(false); }
  };

  const pickCustomer = (c) => {
    set('customer_name', `${c.first_name || ''} ${c.last_name || ''}`.trim());
    set('customer_email', c.email || '');
    set('customer_address', c.address || '');
    setCustSearch('');
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSheet(s => ({ ...s, inv: { ...s.inv, design: { ...s.inv.design, logo_url: ev.target.result } } }));
    reader.readAsDataURL(file);
  };

  const visible = filterStatus === 'all' ? invoices : invoices.filter(i => i.status === filterStatus);
  const totals  = sheet ? calcTotals(sheet.inv.items) : null;
  const custMatches = custSearch.trim() ? customers.filter(c => `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(custSearch.toLowerCase())).slice(0, 5) : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ ...inp({ width: 'auto', padding: '7px 12px' }) }}>
          {['all','draft','sent','paid','overdue','cancelled'].map(s => <option key={s} value={s}>{s === 'all' ? (nl ? 'Alle' : 'All') : s}</option>)}
        </select>
        <button onClick={openNew} style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '10px 18px', cursor: 'pointer' }}>
          + {nl ? 'Nieuwe factuur' : 'New invoice'}
        </button>
      </div>

      {loading ? <div style={{ color: C.silver, textAlign: 'center', padding: 32 }}>...</div> : visible.length === 0 ? (
        <div style={{ color: C.silver, textAlign: 'center', padding: 40, fontSize: 14 }}>
          {nl ? 'Geen facturen gevonden.' : 'No invoices found.'}
        </div>
      ) : visible.map(inv => {
        const t = calcTotals(typeof inv.items === 'string' ? JSON.parse(inv.items || '[]') : (inv.items || []));
        return (
          <div key={inv.id} style={{ ...panel(), cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }} onClick={() => openEdit(inv)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>{inv.invoice_number}</span>
                <StatusBadge status={inv.status} />
              </div>
              <div style={{ color: C.silver, fontSize: 12 }}>{inv.customer_name} · {inv.issue_date}</div>
            </div>
            <div style={{ color: _T.accent, fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{fmtEuro(t.total)}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={e => { e.stopPropagation(); printInvoice(inv, business); }}
                style={{ background: F.panel, border: `1px solid ${F.border}`, borderRadius: 8, color: C.silver, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}>🖨</button>
              <button onClick={e => { e.stopPropagation(); handleDelete(inv.id); }}
                style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer', padding: '4px 8px' }}>✕</button>
            </div>
          </div>
        );
      })}

      {/* Invoice designer sheet */}
      <Sheet open={!!sheet} onClose={() => setSheet(null)} title={sheet?.mode === 'new' ? (nl ? 'Nieuwe factuur' : 'New invoice') : (nl ? 'Factuur bewerken' : 'Edit invoice')}>
        {sheet && (
          <>
            {/* Design Panel */}
            <div style={panel()}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <span style={label}>{nl ? 'LOGO' : 'LOGO'}</span>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                  <button onClick={() => fileRef.current?.click()} style={{ background: F.input, border: `1px solid ${F.inputBorder}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
                    {sheet.inv.design?.logo_url ? (nl ? '✓ Logo geladen' : '✓ Logo loaded') : (nl ? 'Logo uploaden' : 'Upload logo')}
                  </button>
                </div>
                <div>
                  <span style={label}>{nl ? 'KLEUR' : 'COLOR'}</span>
                  <input type="color" value={sheet.inv.design?.primary_color || '#5B9CF6'}
                    onChange={e => setSheet(s => ({ ...s, inv: { ...s.inv, design: { ...s.inv.design, primary_color: e.target.value } } }))}
                    style={{ width: 36, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'none' }} />
                </div>
                <div>
                  <span style={label}>{nl ? 'LAYOUT' : 'LAYOUT'}</span>
                  <select value={sheet.inv.design?.layout || 'classic'}
                    onChange={e => setSheet(s => ({ ...s, inv: { ...s.inv, design: { ...s.inv.design, layout: e.target.value } } }))}
                    style={{ ...inp({ width: 'auto', padding: '7px 12px' }) }}>
                    <option value="classic">{nl ? 'Klassiek' : 'Classic'}</option>
                    <option value="modern">{nl ? 'Modern' : 'Modern'}</option>
                    <option value="minimal">{nl ? 'Minimaal' : 'Minimal'}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Header fields */}
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'FACTUURNUMMER' : 'INVOICE NUMBER'}</span>
                <input style={inp()} value={sheet.inv.invoice_number} onChange={e => set('invoice_number', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'STATUS' : 'STATUS'}</span>
                <select style={inp()} value={sheet.inv.status} onChange={e => set('status', e.target.value)}>
                  {['draft','sent','paid','overdue','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'DATUM' : 'ISSUE DATE'}</span>
                <input type="date" style={inp()} value={sheet.inv.issue_date} onChange={e => set('issue_date', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'VERVALDATUM' : 'DUE DATE'}</span>
                <input type="date" style={inp()} value={sheet.inv.due_date} onChange={e => set('due_date', e.target.value)} />
              </div>
            </div>

            {/* Customer */}
            <div style={panel()}>
              <span style={label}>{nl ? 'KLANT' : 'CUSTOMER'}</span>
              <input style={inp({ marginBottom: 4 })} placeholder={nl ? 'Zoek klant...' : 'Search customer...'}
                value={custSearch} onChange={e => setCustSearch(e.target.value)} />
              {custMatches.map(c => (
                <div key={c.id} onClick={() => pickCustomer(c)}
                  style={{ background: _T.bg3, borderRadius: 8, padding: '8px 12px', marginBottom: 4, cursor: 'pointer', fontSize: 13, color: C.white }}>
                  {c.first_name} {c.last_name} {c.email ? `· ${c.email}` : ''}
                </div>
              ))}
              <input style={inp({ marginBottom: 6, marginTop: 8 })} placeholder={nl ? 'Naam *' : 'Name *'}
                value={sheet.inv.customer_name} onChange={e => set('customer_name', e.target.value)} />
              <input style={inp({ marginBottom: 6 })} placeholder="Email"
                value={sheet.inv.customer_email} onChange={e => set('customer_email', e.target.value)} />
              <input style={inp()} placeholder={nl ? 'Adres' : 'Address'}
                value={sheet.inv.customer_address} onChange={e => set('customer_address', e.target.value)} />
            </div>

            {/* Line items */}
            <div style={panel()}>
              <span style={label}>{nl ? 'REGELS' : 'LINE ITEMS'}</span>
              {sheet.inv.items.map((it, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                  <input style={inp({ flex: 3 })} placeholder={nl ? 'Omschrijving' : 'Description'}
                    value={it.description} onChange={e => setItem(i, 'description', e.target.value)} />
                  <input type="number" style={inp({ flex: 1 })} placeholder={nl ? 'Aantal' : 'Qty'}
                    value={it.qty} onChange={e => setItem(i, 'qty', e.target.value)} />
                  <input type="number" style={inp({ flex: 1.5 })} placeholder={nl ? 'Prijs' : 'Price'}
                    value={it.unit_price} onChange={e => setItem(i, 'unit_price', e.target.value)} />
                  <select style={inp({ flex: 1 })} value={it.vat_pct} onChange={e => setItem(i, 'vat_pct', e.target.value)}>
                    {[0,6,9,21].map(v => <option key={v} value={v}>{v}%</option>)}
                  </select>
                  <button onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 18, cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}>✕</button>
                </div>
              ))}
              <button onClick={addItem} style={{ background: F.input, border: `1px dashed ${F.border}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '8px', cursor: 'pointer', width: '100%' }}>
                + {nl ? 'Regel toevoegen' : 'Add line'}
              </button>
              {totals && (
                <div style={{ textAlign: 'right', marginTop: 12, color: C.silver, fontSize: 13 }}>
                  <div>{nl ? 'Subtotaal' : 'Subtotal'}: {fmtEuro(totals.subtotal)}</div>
                  <div>BTW: {fmtEuro(totals.vat)}</div>
                  <div style={{ color: _T.accent, fontWeight: 700, fontSize: 16, marginTop: 4 }}>{nl ? 'Totaal' : 'Total'}: {fmtEuro(totals.total)}</div>
                </div>
              )}
            </div>

            {/* Notes */}
            <span style={label}>{nl ? 'NOTITIES' : 'NOTES'}</span>
            <textarea style={{ ...inp(), resize: 'none', marginBottom: 16 }} rows={3}
              value={sheet.inv.notes} onChange={e => set('notes', e.target.value)} />

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => printInvoice(sheet.inv, business)}
                style={{ flex: 1, background: F.panel, border: `1px solid ${F.border}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '12px', cursor: 'pointer' }}>
                🖨 {nl ? 'Afdrukken' : 'Print'}
              </button>
              {sheet.mode === 'edit' && sheet.inv.customer_email && (
                <button onClick={handleSend} disabled={sending}
                  style={{ flex: 1, background: `${_T.accent}15`, border: `1px solid ${_T.accent}40`, borderRadius: 12, color: _T.accent, fontSize: 13, padding: '12px', cursor: 'pointer' }}>
                  {sending ? '...' : (nl ? '✉ Versturen' : '✉ Send')}
                </button>
              )}
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 2, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '12px', cursor: 'pointer' }}>
                {saving ? '...' : (nl ? 'Opslaan' : 'Save')}
              </button>
            </div>
          </>
        )}
      </Sheet>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BILLS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_BILL = { vendor_name: '', description: '', amount: '', vat_amount: '0', currency: 'EUR', bill_date: today(), due_date: '', status: 'unpaid', category: 'supplier', attachment_name: '' };

function BillsTab({ lang, businessId }) {
  const nl = lang === 'nl';
  const [bills, setBills]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet]   = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    const d = await getBills(businessId);
    setBills(Array.isArray(d) ? d : []);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setSheet(s => ({ ...s, bill: { ...s.bill, [k]: v } }));
  const handleFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setSheet(s => ({ ...s, bill: { ...s.bill, attachment_data: ev.target.result, attachment_name: file.name } }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (sheet.mode === 'new') await createBill(businessId, sheet.bill);
      else await updateBill(businessId, sheet.bill.id, sheet.bill);
      setSheet(null);
      await load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(nl ? 'Rekening verwijderen?' : 'Delete bill?')) return;
    await deleteBill(businessId, id);
    await load();
  };

  const totalUnpaid = bills.filter(b => b.status === 'unpaid').reduce((s, b) => s + parseFloat(b.amount || 0), 0);

  return (
    <div>
      <div style={{ ...panel(), display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Openstaand' : 'Outstanding'}</div>
          <div style={{ color: '#C8A84B', fontSize: 20, fontWeight: 700 }}>{fmtEuro(totalUnpaid)}</div>
        </div>
        <button onClick={() => setSheet({ mode: 'new', bill: { ...EMPTY_BILL } })}
          style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '10px 18px', cursor: 'pointer' }}>
          + {nl ? 'Rekening' : 'Add bill'}
        </button>
      </div>

      {loading ? <div style={{ color: C.silver, textAlign: 'center', padding: 32 }}>...</div> : bills.map(b => (
        <div key={b.id} style={{ ...panel(), display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          onClick={() => setSheet({ mode: 'edit', bill: b })}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
              <span style={{ color: C.white, fontWeight: 600, fontSize: 14 }}>{b.vendor_name}</span>
              <StatusBadge status={b.status} />
            </div>
            <div style={{ color: C.silver, fontSize: 12 }}>{b.category} · {b.bill_date}</div>
          </div>
          <div style={{ color: '#C8A84B', fontWeight: 700, fontSize: 15, marginRight: 10 }}>{fmtEuro(b.amount)}</div>
          <button onClick={e => { e.stopPropagation(); handleDelete(b.id); }}
            style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>
      ))}

      <Sheet open={!!sheet} onClose={() => setSheet(null)} title={sheet?.mode === 'new' ? (nl ? 'Rekening toevoegen' : 'Add bill') : (nl ? 'Rekening bewerken' : 'Edit bill')}>
        {sheet && (
          <>
            <div style={row}>
              <div style={{ flex: 2 }}>
                <span style={label}>{nl ? 'LEVERANCIER *' : 'VENDOR *'}</span>
                <input style={inp()} value={sheet.bill.vendor_name} onChange={e => set('vendor_name', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'CATEGORIE' : 'CATEGORY'}</span>
                <select style={inp()} value={sheet.bill.category} onChange={e => set('category', e.target.value)}>
                  {BILL_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <span style={label}>{nl ? 'OMSCHRIJVING' : 'DESCRIPTION'}</span>
            <input style={{ ...inp(), marginBottom: 12 }} value={sheet.bill.description} onChange={e => set('description', e.target.value)} />
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'BEDRAG *' : 'AMOUNT *'}</span>
                <input type="number" style={inp()} value={sheet.bill.amount} onChange={e => set('amount', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'BTW BEDRAG' : 'VAT AMOUNT'}</span>
                <input type="number" style={inp()} value={sheet.bill.vat_amount} onChange={e => set('vat_amount', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'VALUTA' : 'CURRENCY'}</span>
                <select style={inp()} value={sheet.bill.currency} onChange={e => set('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'DATUM' : 'DATE'}</span>
                <input type="date" style={inp()} value={sheet.bill.bill_date} onChange={e => set('bill_date', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'VERVALDATUM' : 'DUE DATE'}</span>
                <input type="date" style={inp()} value={sheet.bill.due_date || ''} onChange={e => set('due_date', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'STATUS' : 'STATUS'}</span>
                <select style={inp()} value={sheet.bill.status} onChange={e => set('status', e.target.value)}>
                  {['unpaid','paid','disputed'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <span style={label}>{nl ? 'BIJLAGE' : 'ATTACHMENT'}</span>
            <div style={{ marginBottom: 16 }}>
              <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFile} />
              <button onClick={() => fileRef.current?.click()}
                style={{ background: F.input, border: `1px solid ${F.inputBorder}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '8px 14px', cursor: 'pointer' }}>
                {sheet.bill.attachment_name || (nl ? 'Bestand kiezen...' : 'Choose file...')}
              </button>
            </div>
            <button onClick={handleSave} disabled={saving}
              style={{ width: '100%', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '13px', cursor: 'pointer', marginBottom: 16 }}>
              {saving ? '...' : (nl ? 'Opslaan' : 'Save')}
            </button>
          </>
        )}
      </Sheet>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPENSES TAB
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_EXP = { description: '', amount: '', vat_amount: '0', currency: 'EUR', expense_date: today(), category: 'other' };

function ExpensesTab({ lang, businessId, currentUserId }) {
  const nl = lang === 'nl';
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    const d = await getExpenses(businessId);
    setExpenses(Array.isArray(d) ? d : []);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setSheet(s => ({ ...s, exp: { ...s.exp, [k]: v } }));
  const handleFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setSheet(s => ({ ...s, exp: { ...s.exp, attachment_data: ev.target.result, attachment_name: file.name } }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (sheet.mode === 'new') await createExpense(businessId, { ...sheet.exp, user_id: currentUserId });
      else await updateExpense(businessId, sheet.exp.id, sheet.exp);
      setSheet(null);
      await load();
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(nl ? 'Kostenpost verwijderen?' : 'Delete expense?')) return;
    await deleteExpense(businessId, id);
    await load();
  };

  const visible = filterCat === 'all' ? expenses : expenses.filter(e => e.category === filterCat);
  const catTotals = EXPENSE_CATS.reduce((acc, cat) => {
    acc[cat] = expenses.filter(e => e.category === cat).reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    return acc;
  }, {});
  const grandTotal = expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  return (
    <div>
      {/* Summary bar */}
      <div style={{ ...panel(), display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Totaal kosten' : 'Total expenses'}</div>
          <div style={{ color: '#C05050', fontSize: 20, fontWeight: 700 }}>{fmtEuro(grandTotal)}</div>
        </div>
        <button onClick={() => setSheet({ mode: 'new', exp: { ...EMPTY_EXP } })}
          style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '10px 18px', cursor: 'pointer' }}>
          + {nl ? 'Kostenpost' : 'Add expense'}
        </button>
      </div>

      {/* Category bars */}
      <div style={panel()}>
        <span style={label}>{nl ? 'VERDELING PER CATEGORIE' : 'BY CATEGORY'}</span>
        {EXPENSE_CATS.filter(cat => catTotals[cat] > 0).map(cat => {
          const pct = grandTotal > 0 ? (catTotals[cat] / grandTotal) * 100 : 0;
          return (
            <div key={cat} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.silver, marginBottom: 3 }}>
                <span>{cat}</span><span>{fmtEuro(catTotals[cat])}</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: F.border }}>
                <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: _T.accent }} />
              </div>
            </div>
          );
        })}
      </div>

      <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inp({ width: 'auto', padding: '7px 12px', marginBottom: 12 }) }}>
        <option value="all">{nl ? 'Alle categorieën' : 'All categories'}</option>
        {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
      </select>

      {loading ? <div style={{ color: C.silver, textAlign: 'center', padding: 32 }}>...</div> : visible.map(exp => (
        <div key={exp.id} style={{ ...panel(), display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
          onClick={() => setSheet({ mode: 'edit', exp })}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.white, fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{exp.description}</div>
            <div style={{ color: C.silver, fontSize: 12 }}>{exp.category} · {exp.expense_date}</div>
          </div>
          <div style={{ color: '#C05050', fontWeight: 700, fontSize: 15, marginRight: 10 }}>{fmtEuro(exp.amount)}</div>
          <button onClick={e => { e.stopPropagation(); handleDelete(exp.id); }}
            style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer' }}>✕</button>
        </div>
      ))}

      <Sheet open={!!sheet} onClose={() => setSheet(null)} title={sheet?.mode === 'new' ? (nl ? 'Kostenpost toevoegen' : 'Add expense') : (nl ? 'Kostenpost bewerken' : 'Edit expense')}>
        {sheet && (
          <>
            <span style={label}>{nl ? 'OMSCHRIJVING *' : 'DESCRIPTION *'}</span>
            <input style={{ ...inp(), marginBottom: 12 }} value={sheet.exp.description} onChange={e => set('description', e.target.value)} />
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'BEDRAG *' : 'AMOUNT *'}</span>
                <input type="number" style={inp()} value={sheet.exp.amount} onChange={e => set('amount', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>BTW</span>
                <input type="number" style={inp()} value={sheet.exp.vat_amount} onChange={e => set('vat_amount', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'VALUTA' : 'CURRENCY'}</span>
                <select style={inp()} value={sheet.exp.currency} onChange={e => set('currency', e.target.value)}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'DATUM' : 'DATE'}</span>
                <input type="date" style={inp()} value={sheet.exp.expense_date} onChange={e => set('expense_date', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'CATEGORIE' : 'CATEGORY'}</span>
                <select style={inp()} value={sheet.exp.category} onChange={e => set('category', e.target.value)}>
                  {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <span style={label}>{nl ? 'BON / BIJLAGE' : 'RECEIPT / ATTACHMENT'}</span>
            <div style={{ marginBottom: 16 }}>
              <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleFile} />
              <button onClick={() => fileRef.current?.click()}
                style={{ background: F.input, border: `1px solid ${F.inputBorder}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '8px 14px', cursor: 'pointer' }}>
                {sheet.exp.attachment_name || (nl ? 'Bestand kiezen...' : 'Choose file...')}
              </button>
            </div>
            <button onClick={handleSave} disabled={saving}
              style={{ width: '100%', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '13px', cursor: 'pointer', marginBottom: 16 }}>
              {saving ? '...' : (nl ? 'Opslaan' : 'Save')}
            </button>
          </>
        )}
      </Sheet>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEDGER (GROOTBOEK) TAB
// ═══════════════════════════════════════════════════════════════════════════════
function LedgerTab({ lang, businessId }) {
  const nl = lang === 'nl';
  const [journal, setJournal]   = useState([]);
  const [balance, setBalance]   = useState(null);
  const [pl, setPl]             = useState(null);
  const [loading, setLoading]   = useState(true);
  const [ledgerTab, setLedgerTab] = useState('journal');
  const [sheet, setSheet]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [plFrom, setPlFrom]     = useState(new Date().getFullYear() + '-01-01');
  const [plTo, setPlTo]         = useState(today());

  const load = useCallback(async () => {
    setLoading(true);
    const [j, b] = await Promise.all([getJournal(businessId), getLedgerBalance(businessId)]);
    setJournal(Array.isArray(j) ? j : []);
    setBalance(b && !b.error ? b : null);
    setLoading(false);
  }, [businessId]);

  const loadPL = useCallback(async () => {
    const p = await getLedgerPL(businessId, { from: plFrom, to: plTo });
    setPl(p && !p.error ? p : null);
  }, [businessId, plFrom, plTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (ledgerTab === 'pl') loadPL(); }, [ledgerTab, loadPL]);

  const ENTRY_EMPTY = { entry_date: today(), description: '', account_code: '', debit: '', credit: '' };
  const set = (k, v) => setSheet(s => ({ ...s, entry: { ...s.entry, [k]: v } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await createJournalEntry(businessId, sheet.entry);
      setSheet(null);
      await load();
    } finally { setSaving(false); }
  };

  const printLedger = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Grootboek</title>
    <style>body{font-family:Arial,sans-serif;margin:0;padding:40px;color:#111}h1{color:#1DB8C2}table{width:100%;border-collapse:collapse}th{background:#1DB8C2;color:#fff;padding:10px}td{padding:8px;border-bottom:1px solid #eee;font-size:12px}.dr{color:#C05050}.cr{color:#4AC87A}@media print{body{padding:20px}}</style>
    </head><body><h1>Dagboek · ${businessId}</h1><p>Gegenereerd: ${new Date().toLocaleString('nl-NL')}</p>
    <table><tr><th>Datum</th><th>Omschrijving</th><th>Rekening</th><th>Debet</th><th>Credit</th></tr>
    ${journal.map(e => `<tr><td>${e.entry_date}</td><td>${e.description}</td><td>${e.account_code||''}</td><td class="dr">${parseFloat(e.debit||0)>0?fmtEuro(e.debit):''}</td><td class="cr">${parseFloat(e.credit||0)>0?fmtEuro(e.credit):''}</td></tr>`).join('')}
    </table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const LTABS = [
    { id: 'journal', nl: 'Dagboek', en: 'Journal' },
    { id: 'balance', nl: 'Balans', en: 'Balance' },
    { id: 'pl',      nl: 'W&V',    en: 'P&L' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, background: _T.bg4, borderRadius: 10, padding: 3, marginBottom: 14 }}>
        {LTABS.map(t => (
          <button key={t.id} onClick={() => setLedgerTab(t.id)}
            style={{ flex: 1, background: ledgerTab === t.id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : 'transparent', border: 'none', borderRadius: 8, color: ledgerTab === t.id ? '#0a1220' : C.silver, fontSize: 12, fontWeight: 700, padding: '8px', cursor: 'pointer' }}>
            {nl ? t.nl : t.en}
          </button>
        ))}
      </div>

      {ledgerTab === 'journal' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <button onClick={printLedger}
              style={{ background: F.panel, border: `1px solid ${F.border}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '8px 14px', cursor: 'pointer' }}>
              🖨 {nl ? 'Afdrukken' : 'Print'}
            </button>
            <button onClick={() => setSheet({ entry: { ...ENTRY_EMPTY } })}
              style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '10px 18px', cursor: 'pointer' }}>
              + {nl ? 'Boeking' : 'Entry'}
            </button>
          </div>
          {loading ? <div style={{ color: C.silver, textAlign: 'center', padding: 32 }}>...</div> : (
            <div style={panel({ padding: 0, overflow: 'hidden' })}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr 1fr', gap: 0, background: _T.bg4, padding: '10px 14px', borderRadius: '14px 14px 0 0' }}>
                {[nl?'Datum':'Date', nl?'Omschrijving':'Description', nl?'Rekening':'Account', 'Debet', 'Credit'].map((h, i) => (
                  <div key={i} style={{ fontSize: 10, color: C.silver, fontWeight: 700, letterSpacing: 1 }}>{h}</div>
                ))}
              </div>
              {journal.slice(0, 100).map(e => (
                <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr 1fr', gap: 0, padding: '10px 14px', borderBottom: `1px solid ${F.border}` }}>
                  <div style={{ color: C.silver, fontSize: 12 }}>{e.entry_date}</div>
                  <div style={{ color: C.white, fontSize: 13 }}>{e.description}</div>
                  <div style={{ color: C.silver2, fontSize: 12 }}>{e.account_code}</div>
                  <div style={{ color: '#C05050', fontSize: 13, fontWeight: parseFloat(e.debit||0)>0 ? 600 : 400 }}>{parseFloat(e.debit||0)>0 ? fmtEuro(e.debit) : ''}</div>
                  <div style={{ color: '#4AC87A', fontSize: 13, fontWeight: parseFloat(e.credit||0)>0 ? 600 : 400 }}>{parseFloat(e.credit||0)>0 ? fmtEuro(e.credit) : ''}</div>
                </div>
              ))}
              {journal.length === 0 && <div style={{ color: C.silver, textAlign: 'center', padding: 24, fontSize: 13 }}>{nl ? 'Geen boekingen' : 'No entries'}</div>}
            </div>
          )}
        </>
      )}

      {ledgerTab === 'balance' && (
        <div>
          {!balance ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 40 }}>{nl ? 'Geen balansdata beschikbaar.' : 'No balance data available.'}</div>
          ) : (
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1, ...panel() }}>
                <div style={{ color: _T.accent, fontWeight: 700, fontSize: 13, marginBottom: 12 }}>{nl ? 'ACTIVA' : 'ASSETS'}</div>
                {(balance.assets || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.white, marginBottom: 6 }}>
                    <span>{a.name}</span><span>{fmtEuro(a.amount)}</span>
                  </div>
                ))}
                <div style={{ borderTop: `1px solid ${F.border}`, marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: _T.accent }}>
                  <span>Totaal</span><span>{fmtEuro(balance.total_assets)}</span>
                </div>
              </div>
              <div style={{ flex: 1, ...panel() }}>
                <div style={{ color: '#C05050', fontWeight: 700, fontSize: 13, marginBottom: 12 }}>{nl ? 'PASSIVA' : 'LIABILITIES'}</div>
                {(balance.liabilities || []).map((l, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.white, marginBottom: 6 }}>
                    <span>{l.name}</span><span>{fmtEuro(l.amount)}</span>
                  </div>
                ))}
                <div style={{ borderTop: `1px solid ${F.border}`, marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#C05050' }}>
                  <span>Totaal</span><span>{fmtEuro(balance.total_liabilities)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {ledgerTab === 'pl' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <span style={label}>{nl ? 'VAN' : 'FROM'}</span>
              <input type="date" style={inp()} value={plFrom} onChange={e => setPlFrom(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <span style={label}>{nl ? 'TOT' : 'TO'}</span>
              <input type="date" style={inp()} value={plTo} onChange={e => setPlTo(e.target.value)} />
            </div>
            <button onClick={loadPL} style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}40`, borderRadius: 10, color: _T.accent, fontSize: 13, padding: '10px 16px', cursor: 'pointer', flexShrink: 0 }}>
              {nl ? 'Berekenen' : 'Calculate'}
            </button>
          </div>
          {!pl ? (
            <div style={{ color: C.silver, textAlign: 'center', padding: 40 }}>{nl ? 'Geen data beschikbaar.' : 'No data available.'}</div>
          ) : (
            <div style={panel()}>
              {[
                { label: nl ? 'Omzet (facturen)' : 'Revenue (invoices)', val: pl.revenue, color: '#4AC87A' },
                { label: nl ? 'Kosten' : 'Expenses', val: pl.expenses, color: '#C05050' },
                { label: nl ? 'Rekeningen' : 'Bills', val: pl.bills, color: '#C8A84B' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${F.border}` }}>
                  <span style={{ color: C.silver, fontSize: 13 }}>{item.label}</span>
                  <span style={{ color: item.color, fontWeight: 700, fontSize: 15 }}>{fmtEuro(item.val)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0 0' }}>
                <span style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>{nl ? 'Nettoresultaat' : 'Net result'}</span>
                <span style={{ color: parseFloat(pl.net || 0) >= 0 ? '#4AC87A' : '#C05050', fontWeight: 800, fontSize: 20 }}>{fmtEuro(pl.net)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Journal entry sheet */}
      <Sheet open={!!sheet} onClose={() => setSheet(null)} title={nl ? 'Journaalpost' : 'Journal entry'}>
        {sheet && (
          <>
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'DATUM' : 'DATE'}</span>
                <input type="date" style={inp()} value={sheet.entry.entry_date} onChange={e => set('entry_date', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>{nl ? 'REKENINGNUMMER' : 'ACCOUNT CODE'}</span>
                <input style={inp()} placeholder="b.v. 4000" value={sheet.entry.account_code} onChange={e => set('account_code', e.target.value)} />
              </div>
            </div>
            <span style={label}>{nl ? 'OMSCHRIJVING *' : 'DESCRIPTION *'}</span>
            <input style={{ ...inp(), marginBottom: 12 }} value={sheet.entry.description} onChange={e => set('description', e.target.value)} />
            <div style={row}>
              <div style={{ flex: 1 }}>
                <span style={label}>DEBET</span>
                <input type="number" style={inp()} placeholder="0.00" value={sheet.entry.debit} onChange={e => set('debit', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={label}>CREDIT</span>
                <input type="number" style={inp()} placeholder="0.00" value={sheet.entry.credit} onChange={e => set('credit', e.target.value)} />
              </div>
            </div>
            <button onClick={handleSave} disabled={saving}
              style={{ width: '100%', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '13px', cursor: 'pointer', marginBottom: 16 }}>
              {saving ? '...' : (nl ? 'Boeken' : 'Post entry')}
            </button>
          </>
        )}
      </Sheet>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONNECTIONS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function ConnectionsTab({ lang, businessId }) {
  const nl = lang === 'nl';
  const [connections, setConnections] = useState({});
  const [loading, setLoading]         = useState(true);
  const [forms, setForms]             = useState({});
  const [syncing, setSyncing]         = useState({});
  const [saving, setSaving]           = useState({});
  const [disconnecting, setDisconnecting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const d = await getAccountingConnections(businessId);
    const map = {};
    if (Array.isArray(d)) d.forEach(c => { map[c.provider] = c; });
    setConnections(map);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const setField = (provider, key, val) => setForms(f => ({ ...f, [provider]: { ...(f[provider] || {}), [key]: val } }));

  const handleSave = async (provider) => {
    setSaving(s => ({ ...s, [provider]: true }));
    await saveAccountingConnection(businessId, { provider, ...forms[provider] });
    await load();
    setSaving(s => ({ ...s, [provider]: false }));
  };

  const handleSync = async (provider) => {
    setSyncing(s => ({ ...s, [provider]: true }));
    const r = await syncAccountingConnection(businessId, { provider });
    alert(r.ok ? (nl ? '✓ Synchronisatie geslaagd!' : '✓ Sync successful!') : (nl ? 'Synchronisatie mislukt.' : 'Sync failed.'));
    setSyncing(s => ({ ...s, [provider]: false }));
    await load();
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(nl ? `${provider} ontkoppelen?` : `Disconnect ${provider}?`)) return;
    setDisconnecting(s => ({ ...s, [provider]: true }));
    await deleteAccountingConnection(businessId, provider);
    await load();
    setDisconnecting(s => ({ ...s, [provider]: false }));
  };

  return (
    <div>
      <div style={{ color: C.silver, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
        {nl ? 'Verbind jouw administratiesysteem om facturen, kosten en boekingen automatisch te synchroniseren.' : 'Connect your accounting system to automatically sync invoices, expenses and bookings.'}
      </div>
      {loading ? <div style={{ color: C.silver, textAlign: 'center', padding: 32 }}>...</div> : ACCOUNTING_PROVIDERS.map(prov => {
        const conn = connections[prov.id];
        const isActive = conn?.status === 'active';
        const form = forms[prov.id] || {};

        return (
          <div key={prov.id} style={{ ...panel(), marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${prov.color}20`, border: `1px solid ${prov.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                  {prov.icon}
                </div>
                <div>
                  <div style={{ color: C.white, fontWeight: 700, fontSize: 15 }}>{prov.name}</div>
                  {conn && <div style={{ fontSize: 11, color: isActive ? '#4AC87A' : '#C8A84B' }}>{isActive ? (nl ? '● Verbonden' : '● Connected') : (nl ? '○ Niet actief' : '○ Inactive')} {conn.last_synced ? `· ${conn.last_synced.slice(0,10)}` : ''}</div>}
                </div>
              </div>
              {isActive && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleSync(prov.id)} disabled={syncing[prov.id]}
                    style={{ background: `${prov.color}20`, border: `1px solid ${prov.color}40`, borderRadius: 8, color: prov.color, fontSize: 12, padding: '6px 12px', cursor: 'pointer' }}>
                    {syncing[prov.id] ? '...' : '↻ Sync'}
                  </button>
                  <button onClick={() => handleDisconnect(prov.id)} disabled={disconnecting[prov.id]}
                    style={{ background: 'none', border: `1px solid #C05050`, borderRadius: 8, color: '#C05050', fontSize: 12, padding: '6px 12px', cursor: 'pointer' }}>
                    {disconnecting[prov.id] ? '...' : (nl ? 'Ontkoppelen' : 'Disconnect')}
                  </button>
                </div>
              )}
            </div>

            {prov.fields.map(field => (
              <div key={field.key} style={{ marginBottom: 10 }}>
                <span style={label}>{field.label.toUpperCase()}</span>
                <input type={field.type || 'text'} style={inp()}
                  placeholder={isActive ? (nl ? '••••••••' : '••••••••') : ''}
                  value={form[field.key] || ''}
                  onChange={e => setField(prov.id, field.key, e.target.value)} />
              </div>
            ))}
            <button onClick={() => handleSave(prov.id)} disabled={saving[prov.id]}
              style={{ width: '100%', background: isActive ? `${prov.color}15` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: isActive ? `1px solid ${prov.color}40` : 'none', borderRadius: 10, color: isActive ? prov.color : '#0a1220', fontWeight: 700, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
              {saving[prov.id] ? '...' : isActive ? (nl ? '✓ Opgeslagen' : '✓ Update credentials') : (nl ? 'Verbinden' : 'Connect')}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
export default function BusinessAdministrationScreen({ lang, businessId, business, currentUserId, role }) {
  const nl = lang === 'nl';
  const [tab, setTab] = useState('invoices');

  return (
    <div>
      <style>{CSS}</style>
      {/* Sub-tab strip */}
      <div style={{ display: 'flex', gap: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: 16, paddingBottom: 2 }}>
        {SUBTABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.id ? _T.accent : 'transparent'}`, padding: '8px 12px 6px', cursor: 'pointer', color: tab === t.id ? _T.accent : C.silver, fontSize: 12, fontWeight: tab === t.id ? 700 : 400, whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
            {nl ? t.nl : t.en}
          </button>
        ))}
      </div>

      {tab === 'invoices'    && <InvoicesTab    lang={lang} businessId={businessId} business={business} />}
      {tab === 'bills'       && <BillsTab       lang={lang} businessId={businessId} />}
      {tab === 'expenses'    && <ExpensesTab    lang={lang} businessId={businessId} currentUserId={currentUserId} />}
      {tab === 'ledger'      && <LedgerTab      lang={lang} businessId={businessId} />}
      {tab === 'connections' && <ConnectionsTab lang={lang} businessId={businessId} />}
    </div>
  );
}
